import { addCalendarDays, cachedFormatter, calendarDayInZone, instantFromWallClock, zoneAbbreviation } from '../zonedTime';
import { counted } from '../composites/countedUnit';

/**
 * 🔒 The customer messages a job sends, keyed on the visit (piece 29;
 * decisions 1915, 2080; T1264, T1166, T1167): Booked, On My Way, Reminder and
 * Complete, each switched on or off and its recipients chosen per customer and
 * per Deal. These are the rules and the words that need no database.
 * 🔨 A recipient is a known contact at the customer, never a typed address or
 * number, and no message carries a Confirm or an Ask to Reschedule link.
 */

/** 🔒 The four kinds, in the order a visit sends them. */
export const JOB_MESSAGE_KINDS = ['Booked', 'OnMyWay', 'Reminder', 'Complete'] as const;
export type JobMessageKind = (typeof JOB_MESSAGE_KINDS)[number];

export const JOB_MESSAGE_KIND_LABELS: Readonly<Record<JobMessageKind, string>> = {
  Booked: 'Booked',
  OnMyWay: 'On My Way',
  Reminder: 'Reminder',
  Complete: 'Complete',
};

/** What each kind is for, in one line the rule editor reads beneath its name. */
export const JOB_MESSAGE_KIND_HINTS: Readonly<Record<JobMessageKind, string>> = {
  Booked: 'When a visit is booked and when its window moves: the date, the window, the site and who is going.',
  OnMyWay: 'When the technician taps On My Way: their first name and about how many minutes away they are.',
  Reminder: 'The day before a booked visit, at the hour the Scheduled jobs card names.',
  Complete: 'When the lead completes a visit, with that visit’s signed service report attached to the email.',
};

/** 🔒 The day-before Reminder rides the job clock under its own hour, seeded at 16 in the Organization's zone (decision 1915). */
export const SEND_VISIT_REMINDERS_JOB = 'sendVisitReminders';
export const VISIT_REMINDER_HOUR_KEY = 'workorder.visitReminderHour';
export const VISIT_REMINDER_DEFAULT_HOUR = 16;

/**
 * 🔒 The visits a Reminder run reads (T1167): those whose window starts on
 * tomorrow's calendar day in the Organization's zone, from its midnight to the
 * next one. 🔨 The day is cut in the zone once and moved as a calendar day,
 * so a run near midnight UTC still reads the zone's tomorrow.
 */
export function reminderDayWindow(now: Date, zone: string): { day: string; from: Date; to: Date } {
  const day = addCalendarDays(calendarDayInZone(now, zone), 1);
  return { day, from: instantFromWallClock(`${day}T00:00`, zone), to: instantFromWallClock(`${addCalendarDays(day, 1)}T00:00`, zone) };
}

export function isJobMessageKind(value: unknown): value is JobMessageKind {
  return typeof value === 'string' && (JOB_MESSAGE_KINDS as readonly string[]).includes(value);
}

/**
 * 🔒 The rule in force for a kind (decision 2080): the Deal's own rule for that
 * kind where the job's Deal carries one, else the customer's. A kind with no
 * rule at either level sends nothing.
 */
export function ruleInForce<T extends { dealId: string | null; messageKind: string }>(rules: readonly T[], kind: JobMessageKind, dealId: string | null): T | null {
  const forDeal = dealId === null ? null : rules.find((rule) => rule.messageKind === kind && rule.dealId === dealId) ?? null;
  return forDeal ?? rules.find((rule) => rule.messageKind === kind && rule.dealId === null) ?? null;
}

/** One known contact the rule editor offers: a person employed at the customer, with the address and the mobile a send would use. */
export interface JobMessageContact {
  employmentId: string;
  personId: string;
  name: string;
  title: string | null;
  email: string | null;
  mobile: string | null;
}

/** One stored rule as the editor draws it. */
export interface JobMessageRuleView {
  kind: JobMessageKind;
  /** The rule's version, which a save names so a rule changed meanwhile is met and not overwritten (piece 7; T1363). */
  version: number;
  isEnabled: boolean;
  employmentIds: string[];
  setBy: string | null;
  setAt: string | null;
}

/** 🔒 One kind's row on the editor: the rule at this level, and on a Deal the customer's rule it overrides or follows. */
export interface JobMessageKindRow {
  kind: JobMessageKind;
  own: JobMessageRuleView | null;
  /** On a Deal, the customer's own rule for the kind; always null on the customer's editor. */
  customer: JobMessageRuleView | null;
}

/** What the rule editor reads, on the customer's Field Service tile or under a Deal's Work Orders. */
export interface JobMessageRulesAnswer {
  companyId: string;
  dealId: string | null;
  contacts: JobMessageContact[];
  rows: JobMessageKindRow[];
  canEdit: boolean;
}

/** What a save carries: the kind, on or off, the contacts it sends to, and the version of the rule it was drawn from (none for a rule that is not there yet). */
export interface SaveJobMessageRuleInput {
  kind: JobMessageKind;
  isEnabled: boolean;
  employmentIds: readonly string[];
  version?: number | null;
}

/** 🔒 Why a rule is refused, or null: a known kind, and a switched-on rule names at least one known contact (decision 2080). */
export function jobMessageRuleProblem(input: Partial<SaveJobMessageRuleInput> | null | undefined, contactIds: ReadonlySet<string>): string | null {
  if (!input || !isJobMessageKind(input.kind)) return 'name the message: Booked, On My Way, Reminder or Complete';
  if (typeof input.isEnabled !== 'boolean') return 'say whether the message is on or off';
  const ids = Array.isArray(input.employmentIds) ? input.employmentIds : [];
  const stranger = ids.find((id) => !contactIds.has(id));
  if (stranger !== undefined) return 'a recipient is a contact employed at this customer; a typed address or number is never one';
  if (input.isEnabled && ids.length === 0) return 'a message that is on names at least one contact; switch it off to send nothing';
  return null;
}

/** The facts a message names, every instant drawn in the Organization's zone. */
export interface JobMessageFacts {
  workOrderNumber: string;
  companyName: string;
  siteName: string;
  visitLabel: string;
  windowStart: string;
  windowEnd: string | null;
  zone: string;
  /** Who is going, by first name; empty where nobody is named yet. */
  technicians: readonly string[];
  /** On My Way's minutes, as the technician confirmed them (T1166). */
  minutes: number | null;
}

/** The words one message carries: the mail's subject and body, and the text, which always names the job (T391). */
export interface JobMessageWords {
  subject: string;
  body: string;
  text: string;
}

/** The visit's day in the zone, as a person says it: Tuesday, October 6. */
export function visitDayWords(instant: string, zone: string): string {
  return cachedFormatter(zone, { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(instant));
}

/** The visit's window in the zone, the zone named once: 8:00 AM to 12:00 PM EDT, or from 8:00 AM EDT where no end is booked. */
export function visitWindowWords(start: string, end: string | null, zone: string): string {
  const clock = (instant: string) => cachedFormatter(zone, { hour: 'numeric', minute: '2-digit' }).format(new Date(instant));
  const abbreviation = zoneAbbreviation(start, zone);
  return end ? `${clock(start)} to ${clock(end)} ${abbreviation}` : `from ${clock(start)} ${abbreviation}`;
}

function whoIsGoing(names: readonly string[]): string {
  if (names.length === 0) return 'our technician';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * 🔒 The words of each kind (decisions 1915, 2080). Booked and Reminder name the
 * date, the window, the site, who is going and the job; On My Way names the
 * technician's first name and the minutes they confirmed; Complete names the
 * visit and says its service report is attached. 🔨 No link to confirm or to
 * reschedule: the customer replies to the message, which reaches the desk.
 */
export function jobMessageWords(kind: JobMessageKind, facts: JobMessageFacts): JobMessageWords {
  const job = `Job ${facts.workOrderNumber}`;
  const day = visitDayWords(facts.windowStart, facts.zone);
  const window = visitWindowWords(facts.windowStart, facts.windowEnd, facts.zone);
  const who = whoIsGoing(facts.technicians);
  const reply = 'Reply to this message with any question.';
  if (kind === 'Booked') {
    return {
      subject: `${job}: your visit is booked for ${day}`,
      body: `Your visit ${facts.visitLabel} for ${job} is booked for ${day}, ${window}, at ${facts.siteName}. ${who} will be there.\n\n${reply}`,
      text: `${job}: your visit is booked for ${day}, ${window}, at ${facts.siteName}, with ${who}. ${reply}`,
    };
  }
  if (kind === 'Reminder') {
    return {
      subject: `${job}: your visit is tomorrow, ${day}`,
      body: `A reminder that your visit ${facts.visitLabel} for ${job} is tomorrow, ${day}, ${window}, at ${facts.siteName}. ${who} will be there.\n\n${reply}`,
      text: `${job}: a reminder that your visit is tomorrow, ${day}, ${window}, at ${facts.siteName}, with ${who}. ${reply}`,
    };
  }
  if (kind === 'OnMyWay') {
    const away = facts.minutes === null ? 'on the way' : `on the way, about ${counted(facts.minutes, 'minute')} away`;
    return {
      subject: `${job}: ${who} is on the way`,
      body: `${who} is ${away} from ${facts.siteName} for your visit ${facts.visitLabel}.\n\n${reply}`,
      text: `${job}: ${who} is ${away} from ${facts.siteName}. ${reply}`,
    };
  }
  return {
    subject: `${job}: visit ${facts.visitLabel} is complete`,
    body: `Visit ${facts.visitLabel} for ${job} at ${facts.siteName} is complete. The signed service report is attached.\n\n${reply}`,
    text: `${job}: visit ${facts.visitLabel} at ${facts.siteName} is complete. The signed service report is in your email. ${reply}`,
  };
}

/**
 * 🔒 The revision a text is keyed on at the messaging seam, so a retried send is
 * one message: Booked by the window it names, so a moved window sends again and
 * the same window does not; every other kind once per visit.
 */
export function jobMessageRevision(kind: JobMessageKind, visitId: string, windowStart: string): string {
  return kind === 'Booked' ? `${visitId}.${kind}.${windowStart}` : `${visitId}.${kind}`;
}

/** Whom Service Report: Send opens ticked (decisions 2062, 2080): the Complete rule's recipients, or the interim's tick where no rule is in force. */
export type ReportRecipientsFrom = 'CompleteRule' | 'Ticked';
